import { playerService } from './player-service';
import { roundService, HttpError } from './round-service';
import { courseService } from './course-service';
import { getMyIdentity, setMyIdentity, clearMyIdentity, storePlayerToken } from './player-identity-service';
import { Round, RoundStatus } from '../models/round';
import { Course, Hole } from '../models/course';

class ServiceFacade {
    public async updatePlayerName(name: string) {
        const identity = getMyIdentity();
        if (!identity) throw new Error('No identity found');
        await playerService.updatePlayerName(identity.id, name);
        setMyIdentity({ ...identity, name });
    }

    public async createRound(roundName: string, playerName: string, courseId?: string) {
        try {
            const identity = getMyIdentity();
            let playerId: string;
            let resolvedName: string;

            if (identity) {
                playerId = identity.id;
                resolvedName = identity.name;
                if (identity.token) storePlayerToken(identity.id, identity.token);
                if (playerName.trim() && playerName.trim() !== identity.name) {
                    await playerService.updatePlayerName(identity.id, playerName.trim());
                    resolvedName = playerName.trim();
                    setMyIdentity({ ...identity, name: resolvedName });
                }
            } else {
                const playerResponse = await playerService.createPlayer(playerName);
                playerId = playerResponse.body.id;
                resolvedName = playerResponse.body.name;
                setMyIdentity({ id: playerId, token: playerResponse.body.userToken, name: resolvedName });
            }

            const roundResponse = await roundService.createRound(roundName, playerId, courseId);

            try {
                await roundService.attachPlayerToRound(roundResponse.body.id, playerId);
            } catch (err) {
                if (err instanceof HttpError && err.status === 404) {
                    clearMyIdentity();
                    const freshPlayer = await playerService.createPlayer(playerName);
                    playerId = freshPlayer.body.id;
                    resolvedName = freshPlayer.body.name;
                    setMyIdentity({ id: playerId, token: freshPlayer.body.userToken, name: resolvedName });
                    await roundService.attachPlayerToRound(roundResponse.body.id, playerId);
                } else {
                    throw err;
                }
            }

            return {
                body: {
                    round: roundResponse.body,
                    player: {
                        id: playerId,
                        name: resolvedName,
                    },
                },
                status: roundResponse.status,
            };
        } catch (error) {
            console.error('Error creating round:', error);
            throw error;
        }
    }

    public async getRound(roundId: string, playerId: string) {
        try {
            const response = await roundService.getRound(roundId);

            const round: Round = response.body;
            const player = round.players.find((p) => p.id === playerId);
            round.player = player;

            return round;
        } catch (error) {
            console.error('Error getting round:', error);
            throw error;
        }
    }

    public async updateScore(roundId: string, playerId: string, hole: number, score: number) {
        try {
            await roundService.recordScore(roundId, playerId, hole, score);
            return await this.getRound(roundId, playerId);
        } catch (error) {
            console.error('Error updating score:', error);
            throw error;
        }
    }

    public async completeRound(roundId: string, playerId: string) {
        try {
            await roundService.updatePlayerRoundStatus(roundId, playerId, RoundStatus.COMPLETED);
            return await this.getRound(roundId, playerId);
        } catch (error) {
            console.error('Error completing round:', error);
            throw error;
        }
    }

    public async editRound(roundId: string, playerId: string) {
        try {
            await roundService.updatePlayerRoundStatus(roundId, playerId, RoundStatus.IN_PROGRESS);
            return await this.getRound(roundId, playerId);
        } catch (error) {
            console.error('Error completing round:', error);
            throw error;
        }
    }

    public async getSharedRound(roundId: string) {
        try {
            const response = await roundService.getRound(roundId);
            return response.body;
        } catch (error) {
            console.error('Error getting shared round:', error);
            throw error;
        }
    }

    public async joinRound(roundId: string, playerName: string) {
        try {
            const identity = getMyIdentity();
            let playerId: string;
            let resolvedName: string;

            if (identity) {
                playerId = identity.id;
                resolvedName = identity.name;
                if (identity.token) storePlayerToken(identity.id, identity.token);
                if (playerName.trim() && playerName.trim() !== identity.name) {
                    await playerService.updatePlayerName(identity.id, playerName.trim());
                    resolvedName = playerName.trim();
                    setMyIdentity({ ...identity, name: resolvedName });
                }
            } else {
                const playerResponse = await playerService.createPlayer(playerName);
                playerId = playerResponse.body.id;
                resolvedName = playerResponse.body.name;
                setMyIdentity({ id: playerId, token: playerResponse.body.userToken, name: resolvedName });
            }

            try {
                await roundService.attachPlayerToRound(roundId, playerId);
            } catch (err) {
                if (err instanceof HttpError && err.status === 404) {
                    clearMyIdentity();
                    const freshPlayer = await playerService.createPlayer(playerName);
                    playerId = freshPlayer.body.id;
                    resolvedName = freshPlayer.body.name;
                    setMyIdentity({ id: playerId, token: freshPlayer.body.userToken, name: resolvedName });
                    await roundService.attachPlayerToRound(roundId, playerId);
                } else {
                    throw err;
                }
            }

            return {
                body: {
                    player: {
                        id: playerId,
                        name: resolvedName,
                    },
                },
            };
        } catch (error) {
            console.error('Error joining round:', error);
            throw error;
        }
    }

    public async listCourses() {
        const response = await courseService.listCourses();
        return response.body.courses;
    }

    public async getCourse(id: string): Promise<Course> {
        const response = await courseService.getCourse(id);
        return response.body;
    }

    public async createCourse(name: string, holes: Hole[], playerId: string): Promise<Course> {
        const response = await courseService.createCourse(name, holes, playerId);
        return response.body;
    }

    public async updateCourse(
        id: string,
        updates: { name?: string; holes?: Hole[] },
        playerId: string
    ): Promise<Course> {
        const response = await courseService.updateCourse(id, updates, playerId);
        return response.body;
    }

    public async setHolePar(roundId: string, playerId: string, hole: number, par: number): Promise<Round> {
        try {
            await roundService.setHolePar(roundId, playerId, hole, par);
            return await this.getRound(roundId, playerId);
        } catch (error) {
            console.error('Error setting hole par:', error);
            throw error;
        }
    }
}

export const serviceFacade = new ServiceFacade();
